/**
 * Identity Mode Selector
 *
 * Radio card group for choosing Self / Counterparty / Both analysis modes.
 * Shows recommended products per mode.
 */

import { motion } from 'framer-motion'
import type { IdentityMode } from '@personic/shared'

interface IdentityModeSelectorProps {
  value: IdentityMode
  onChange: (mode: IdentityMode) => void
}

const modes: Array<{
  id: IdentityMode
  title: string
  icon: string
  description: string
  products: string[]
}> = [
  {
    id: 'self_focused',
    title: 'Self-Directed',
    icon: '🪞',
    description:
      'Analyze the invoker\'s own communication patterns. Best for self-improvement and coaching use cases.',
    products: ['Coach Lens', 'Agent Training', 'Self-Assessment'],
  },
  {
    id: 'counterparty_focused',
    title: 'Counterparty',
    icon: '🎯',
    description:
      'Analyze the other party\'s signals to understand their intent, certainty, and commitment levels.',
    products: ['Engage', 'Sales Intelligence', 'Support Analytics'],
  },
  {
    id: 'both',
    title: 'Dual-Stream (Both)',
    icon: '🔄',
    description:
      'Analyze both parties simultaneously with cross-stream correlation. Richest insights, higher compute cost.',
    products: ['Full Analytics', 'Relationship Mapping', 'Negotiation'],
  },
]

export default function IdentityModeSelector({
  value,
  onChange,
}: IdentityModeSelectorProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Identity Mode</h3>
        <p className="text-sm text-gray-500 mt-1">
          Choose which conversation participants to analyze
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {modes.map((mode) => {
          const isSelected = value === mode.id
          return (
            <motion.button
              key={mode.id}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onChange(mode.id)}
              className={`relative text-left p-5 rounded-xl border-2 transition-colors ${
                isSelected
                  ? 'border-engage-primary bg-engage-primary/5 ring-2 ring-engage-primary/20'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              {isSelected && (
                <div className="absolute top-3 right-3 w-5 h-5 bg-engage-primary rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}

              <div className="text-3xl mb-3">{mode.icon}</div>
              <h4 className="font-semibold text-gray-900">{mode.title}</h4>
              <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                {mode.description}
              </p>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {mode.products.map((product) => (
                  <span
                    key={product}
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      isSelected
                        ? 'bg-engage-primary/10 text-engage-primary'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {product}
                  </span>
                ))}
              </div>
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}
